import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const NotificationContext = createContext();

export function useNotification() {
  return useContext(NotificationContext);
}

/**
 * Notification types:
 * - 'info'    (blue/gray)
 * - 'success' (green)
 * - 'warning' (yellow/orange)
 * - 'error'   (red)
 */
const DEFAULT_DURATION = 4000;

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const idCounter = useRef(0);

  const addNotification = useCallback((message, type = 'info', duration = DEFAULT_DURATION) => {
    const id = ++idCounter.current;
    setNotifications(prev => [...prev, { id, message, type, duration }]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, duration);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {/* Render the notification stack */}
      <NotificationStack
        notifications={notifications}
        onDismiss={removeNotification}
      />
    </NotificationContext.Provider>
  );
}

/**
 * Renders the stack of popup notifications fixed at the top-right of the viewport.
 */
function NotificationStack({ notifications, onDismiss }) {
  if (notifications.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 2147483647,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      maxWidth: 380,
      width: '100%',
      pointerEvents: 'none',
    }}>
      {notifications.map((notification, index) => (
        <PopupNotification
          key={notification.id}
          notification={notification}
          onDismiss={() => onDismiss(notification.id)}
          index={index}
        />
      ))}
    </div>
  );
}

/**
 * Individual popup notification with slide-in animation.
 */
function PopupNotification({ notification, onDismiss, index }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger slide-in animation on mount.
    // Use a small timeout as a fallback in case rAF is not available.
    const frame = requestAnimationFrame(() => setVisible(true));
    const fallback = setTimeout(() => setVisible(true), 50);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
    };
  }, []);

  const typeStyles = {
    info: {
      background: 'rgba(30, 41, 59, 0.95)',
      border: '1px solid rgba(148, 163, 184, 0.3)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
      ),
    },
    success: {
      background: 'rgba(22, 163, 74, 0.95)',
      border: '1px solid rgba(74, 222, 128, 0.3)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
      ),
    },
    warning: {
      background: 'rgba(217, 119, 6, 0.95)',
      border: '1px solid rgba(251, 191, 36, 0.3)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      ),
    },
    error: {
      background: 'rgba(220, 38, 38, 0.95)',
      border: '1px solid rgba(248, 113, 113, 0.3)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      ),
    },
  };

  const style = typeStyles[notification.type] || typeStyles.info;

  return (
    <div
      style={{
        background: style.background,
        border: style.border,
        borderRadius: 12,
        padding: '12px 16px',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        pointerEvents: 'auto',
        cursor: 'pointer',
        transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        transform: visible ? 'translateX(0) scale(1)' : 'translateX(120%) scale(0.9)',
        opacity: visible ? 1 : 0,
      }}
      onClick={onDismiss}
      role="alert"
    >
      <div style={{ flexShrink: 0, marginTop: 1 }}>
        {style.icon}
      </div>
      <div style={{
        flex: 1,
        color: '#f1f5f9',
        fontSize: 14,
        lineHeight: 1.5,
        fontWeight: 500,
        wordBreak: 'break-word',
      }}>
        {notification.message}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.4)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
        }}
        aria-label="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

export default NotificationContext;
